/* ============================================================
   Fence Works — Usage-credit model (single source of truth)
   ------------------------------------------------------------
   Pure data + helpers, no React. Published on window.VFP_CREDITS.

   WHAT'S A CREDIT? A credit is one billable action.
     · 1 SMS (sent OR received)                       = 1 credit
     · 1 AI text action (reply/draft/caption/parse)   = 1 credit
     · 1 property/satellite lookup                    = 3 credits
     · 1 AI render                                    = 10 credits
     · email                                          = free

   ECONOMICS — ≈ $0.01–0.012 of metered cost per credit (SMS is the
   costliest credit at ~$0.012: Twilio A2P + carrier fees + amortized
   number). Other per-unit COGS for reference:
     · AI voice minute  ≈ $0.12  (Retell + telephony)
     · AI text action   ≈ $0.005 (draft / caption / chat-turn / lead-parse)
     · property lookup  ≈ $0.03  (geocode + aerial imagery + parcel)
     · AI fence render  ≈ $0.10
     · email (Resend)   ≈ $0.0004 → treated as FREE
     · phone number rent ≈ $1.15/mo (fixed, not metered as credits)

   Add-on packs are priced at ≥2× our cost. Plan allowances are
   right-sized to typical usage so heavy users upgrade or buy packs.

   Voice is metered SEPARATELY as talk-minutes (the AI Front Desk already
   surfaces "talk-minutes"), NOT as credits. If voice were ever unified
   into the credit pool, 1 voice minute ≈ 12 credits ($0.12 / $0.01).
   ============================================================ */

// Action → credit cost. 1 credit ≈ $0.01 of metered COGS.
export const COST: Record<string, number> = {
  sms: 1,       // send OR receive a text segment (~$0.012)
  ai_text: 1,   // review-draft / caption / web-chat AI reply turn / lead parse (~$0.005)
  concrete_scan: 3,   // a vision read of up to six concrete photos (§13 item 10; ~$0.02–0.03)
  lookup: 3,    // satellite / property estimate pull (~$0.03)
  render: 10,   // AI fence render (~$0.10) — paid plans
  email: 0,     // ~$0.0004 → free
};

// Voice is metered as minutes, not credits. Kept here for reference / future unify.
export const VOICE_CREDITS_PER_MIN = 12;   // 1 min ≈ $0.12 ≈ 12 credits if ever unified

// Plan monthly allowances: included credits + included AI talk-minutes.
export const ALLOW: Record<string, { credits: number; voiceMin: number }> = {
  free:       { credits: 0,    voiceMin: 0 },
  starter:    { credits: 250,  voiceMin: 0 },
  pro:        { credits: 1000, voiceMin: 90 },
  growth:     { credits: 6000, voiceMin: 800 },
  enterprise: { credits: 6000, voiceMin: 800 },   // retired name; same allowance as Growth
};

// Trials include 10% of the plan's monthly allowance so AI texting/replies
// can be tested risk-free (e.g. Pro trial = 100 credits / 9 min).
export const TRIAL_PCT = 0.1;

// Add-on credit packs (one-time top-ups). price = $ USD.
export const PACKS: { id: string; credits: number; price: number }[] = [
  { id: "pack500", credits: 500,  price: 15 },   // $0.030/cr (2.5×)
  { id: "pack2k",  credits: 2000, price: 50 },   // $0.025/cr (2.08×)
  { id: "pack5k",  credits: 5000, price: 120 },  // $0.024/cr (2.0×)
];

export const SEAT_PRICE = 5;        // $/seat/mo for add-on seats
export const VOICE_OVERAGE = 0.15;  // $/min over the included talk-minutes

// ---- helpers (tiny, dependency-free) ----

// Monthly allowance for a plan (falls back to free).
export function planAllow(planId: string) {
  return ALLOW[planId] || ALLOW.free;
}

// Trial allowance = 10% of the plan's monthly allowance, rounded.
export function trialAllow(planId: string) {
  const a = planAllow(planId);
  return { credits: Math.round(a.credits * TRIAL_PCT), voiceMin: Math.round(a.voiceMin * TRIAL_PCT) };
}

// Credit cost of an action (unknown → 0).
export function costOf(action: string) {
  return COST[action] || 0;
}

// Format a credit count, e.g. 1250 → "1,250".
export function fmtCredits(n: number) {
  return Math.round(n || 0).toLocaleString("en-US");
}

// Percent of an allowance used (0–100, clamped). total<=0 → 0.
export function pctUsed(used: number, total: number) {
  if (!total || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((used / total) * 100)));
}

// ---- runtime: balance, trial detection & enforcement ----------------------
// The SERVER is the source of truth for usage/billing (it owns Twilio/Retell),
// so it does the real metering and the hard cutoff. These client helpers mirror
// it for UX — reflect limits and BLOCK an over-budget action before it fires —
// and apply the SAME 10% trial cap so a trial can never run up cost. Every
// caller is defensive: when VFP_CREDITS is absent the gates no-op to ALLOWED.

// On a trial? True during a Stripe trial (settings.subStatus === "trialing"),
// or when forced via settings.trialing (demo/testing). Trials use trialAllow().
export function isTrial(store: any) {
  const s = (store && store.settings) || {};
  return s.subStatus === "trialing" || !!s.trialing;
}

// Current credit + talk-minute position. Prefers a server usage snapshot
// (settings.usageCache, refreshed from DB.billingUsage) and otherwise derives
// the allowance from the plan — trial-capped when on a trial. Purchased packs
// (settings.creditBalance) add to the credit total; settings.creditUsage is the
// demo/offline accrual.
export function creditState(store: any) {
  const s = (store && store.settings) || {};
  const planId = s.plan || "free";
  const trial = isTrial(store);
  const base = trial ? trialAllow(planId) : planAllow(planId);
  const snap = s.usageCache || null;       // { credits:{used,total}, voiceMin:{used,total}, balance }
  const purchased = Number(s.creditBalance) || 0;
  const local = s.creditUsage || { credits: 0, voiceMin: 0 };
  const has = (x: any) => snap && x && x.total != null;
  const creditsTotal = has(snap && snap.credits) ? Number(snap.credits.total) + (Number(snap.balance) || 0) : base.credits + purchased;
  const creditsUsed = has(snap && snap.credits) ? Number(snap.credits.used) : Number(local.credits) || 0;
  const minTotal = has(snap && snap.voiceMin) ? Number(snap.voiceMin.total) : base.voiceMin;
  const minUsed = has(snap && snap.voiceMin) ? Number(snap.voiceMin.used) : Number(local.voiceMin) || 0;
  return {
    planId, trial,
    creditsTotal, creditsUsed, creditsLeft: Math.max(0, creditsTotal - creditsUsed),
    minTotal, minUsed, minLeft: Math.max(0, minTotal - minUsed),
  };
}

// Can the account afford `qty` of a credit `action` right now?
export function canSpend(store: any, action: string, qty = 1) {
  return creditState(store).creditsLeft >= costOf(action) * Math.max(1, qty);
}
// Can it afford `mins` of AI talk-time? (voice is metered in minutes, not credits)
export function canSpendMinutes(store: any, mins = 1) {
  return creditState(store).minLeft >= Math.max(0, mins);
}

// Record demo usage (offline / no live service). Live billing is metered
// server-side, so skip the local accrual when a server snapshot is present.
export function spend(store: any, action: string, qty = 1) {
  if (!store || !store.setSettings) return;
  const s = store.settings || {};
  if (s.usageCache) return;                // server is the meter of record
  const u = s.creditUsage || { credits: 0, voiceMin: 0 };
  store.setSettings({ creditUsage: { ...u, credits: (Number(u.credits) || 0) + costOf(action) * Math.max(1, qty) } });
}

// Default block copy for an exhausted budget (trial vs paid).
export function blockMsg(store: any) {
  return creditState(store).trial
    ? "You've used up your trial credits. Upgrade to a paid plan to keep texting & running AI."
    : "You're out of credits. Buy a credit pack or upgrade your plan to keep going.";
}

// Gate a money-costing action. Returns true if allowed (recording demo usage);
// otherwise surfaces the block — via opts.onBlock, else alert() + optional
// redirect to the plans tab through opts.go — and returns false. Free actions
// and a missing model both no-op to ALLOWED.
export function guard(store: any, action: string, opts: any = {}) {
  if (costOf(action) === 0) return true;
  const qty = opts.qty || 1;
  if (canSpend(store, action, qty)) { if (opts.record !== false) spend(store, action, qty); return true; }
  const msg = blockMsg(store);
  if (opts.onBlock) opts.onBlock(msg);
  else { try { const n = (window as any).VFP_NOTIFY; n ? n(msg, { kind: "error", ms: 9000 }) : alert(msg); } catch (e) {} if (opts.go) opts.go("account", { tab: "plan" }); }
  return false;
}

export const VFP_CREDITS = {
  COST, ALLOW, PACKS, SEAT_PRICE, VOICE_OVERAGE, TRIAL_PCT, VOICE_CREDITS_PER_MIN,
  planAllow, trialAllow, costOf, fmtCredits, pctUsed,
  isTrial, creditState, canSpend, canSpendMinutes, spend, blockMsg, guard,
};

Object.assign(window, { VFP_CREDITS });
